import React from 'react';
import styled from '@emotion/styled';
import { Table, Tooltip } from 'antd';
import { colorConstants } from 'config/colorConstants';
import tw from 'twin.macro';
import CrossAlt from 'assets/svg/CrossAlt';
import { comparisonTileData } from 'components/organism/PriceSimulator/Comparison/Comparison';
import { NegativeTriangle, PositiveTriangle } from 'assets/svg';
import { formatNumWithKMB, formatNumWithKMBAlt } from 'utils/helperMethods';
import { PSR_COMP_SCENARIO_CARD_CLICKED } from 'utils/mixpanelEventNames';
import { trackEventMixpanel } from 'utils/mixpanel';

type ScenarioCardType = {
    scenarioId: number;
    tableId?: string;
    cardDetails: any;
    selected?: boolean;
    setSelectedCard: Function;
    onRemoveCardHandler: Function;
    comparisonTile?: any;
    orderedSelectedInputKpiOpitions?: any;
};
const ScenarioCard = ({
    tableId = '',
    cardDetails,
    selected,
    setSelectedCard,
    scenarioId,
    onRemoveCardHandler,
    comparisonTile,
    orderedSelectedInputKpiOpitions,
}: ScenarioCardType) => {
    const columns: any = [
        {
            title: 'Actual',
            dataIndex: 'value',
            onCell: (record: any, rowIndex: any) => {
                let style: React.CSSProperties = { padding: '10px 0px', border: '' };
                return { style };
            },
            render: (columnData: any, rowData: any, rowIndex: number) => {
                return (
                    <div className="flex items-center justify-center">
                        <span className="font-xs text-center">{formatNumWithKMBAlt(Math.abs(columnData), 2, 2)}</span>
                    </div>
                );
            },
        },
        {
            title: 'Abs Chg',
            dataIndex: 'absChg',
            onCell: (record: any, rowIndex: any) => {
                let style: React.CSSProperties = { padding: '10px 4px', border: '' };
                return { style };
            },
            render: (columnData: any, rowData: any, rowIndex: number) => {
                return (
                    <div className="flex items-center justify-center">
                        <span className="font-xs text-center">{formatNumWithKMBAlt(Math.abs(columnData), 2, 2)}</span>
                    </div>
                );
            },
        },
        {
            title: '% Chg',
            dataIndex: 'percentageChg',
            onCell: (record: any, rowIndex: any) => {
                let style: React.CSSProperties = { padding: '10px 4px', border: '' };
                return { style };
            },
            render: (columnData: any, rowData: any, rowIndex: number) => {
                return (
                    <div className="flex items-center justify-start">
                        <span className="mr-2">
                            {Math.abs(Number(columnData)) > 0 &&
                                (Number(columnData) >= 0 ? <PositiveTriangle /> : <NegativeTriangle />)}
                        </span>
                        <span className="font-xs">{formatNumWithKMBAlt(Math.abs(columnData), 2, 2)}%</span>
                    </div>
                );
            },
        },
    ];

    if (comparisonTile.key === comparisonTileData[1].key) {
        columns[0].title = 'Goal';
    }

    const allScenariosDataSet =
        (cardDetails?.data?.scenarioComparisonData && cardDetails?.data?.scenarioComparisonData[comparisonTile.key]) ||
        [];

    const selectedKpis = orderedSelectedInputKpiOpitions?.map((item: any) => item.label);

    const commonLoading = cardDetails?.loading;

    return (
        <div
            className={`flex flex-col mr-2 ${
                selected ? 'cursor-default' : 'cursor-pointer'
            }  border rounded overflow-hidden  ${selected ? 'border-purpleFont' : 'border-white'}  `}
            style={{ minWidth: '226px', maxWidth: '226px' }}
            onClick={() => {
                setSelectedCard(scenarioId);
                trackEventMixpanel(PSR_COMP_SCENARIO_CARD_CLICKED, {
                    scenarioId,
                });
            }}
            key={scenarioId}
        >
            <div
                className="flex bg-bgBackground py-2 text-purpleFont font-bold justify-between items-center px-4"
                style={{ border: `1px solid ${colorConstants.greyAlt3}`, borderBottom: 'none' }}
            >
                <Tooltip
                    placement="top"
                    color="white"
                    destroyTooltipOnHide
                    overlayStyle={{ borderRadius: 4 }}
                    overlayInnerStyle={{
                        color: colorConstants.darkGrey,
                    }}
                    title={cardDetails?.scenarioName}
                >
                    <span className=" text-center text-ellipsis ">
                        {cardDetails?.scenarioName.substring(0, 20)}
                        {cardDetails?.scenarioName.length > 20 ? '...' : ''}
                    </span>
                </Tooltip>
                <span
                    onClick={(e) => {
                        !selected && onRemoveCardHandler(scenarioId);
                        e.stopPropagation();
                    }}
                    className={` ${selected ? 'cursor-not-allowed' : ''}`}
                >
                    <CrossAlt />
                </span>
            </div>
            <CustomTablePriceScenarioCard
                bordered
                style={{
                    opacity: commonLoading ? '0.5' : 1,
                    pointerEvents: commonLoading ? 'none' : 'auto',
                    height: '100%',
                }}
                columns={columns}
                pagination={false}
                scroll={{ x: '100%', y: commonLoading ? 0 : 270 }}
                className=""
                sticky
                loading={commonLoading}
                dataSource={allScenariosDataSet.filter((d: any) => selectedKpis?.includes(d.KPI))}
                id={tableId}
            />
        </div>
    );
};

export default ScenarioCard;

export const CustomTablePriceScenarioCard = styled(Table)`
    .ant-table-sticky-scroll {
        position: static;
        display: none;
    }

    .ant-table-tbody > tr > td,
    .ant-table-thead > tr > th,
    .ant-table tfoot > tr > td {
        padding: 10px;
    }

    .ant-table-thead > tr > th {
        color: ${colorConstants.darkGrey};
        background: #fafafc;
        padding: 0px;
        height: 27px;
        text-align: center;
        font-size: 12px;
    }

    .ant-table-header col:not(:last-child),
    .ant-table-body col {
        min-width: 50px;
    }

    .ant-table-thead
        > tr
        > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not(
            [colspan]
        ):before {
        background: none;
    }
    .ant-table-column-sorter {
        position: relative;
        left: -10px;
    }
    .ant-table-column-sorter-down.active,
    .ant-table-column-sorter-up.active {
        ${tw`text-purpleFont`};
    }
    .ant-table-cell {
        text-align: center;
    }

    border: ${'1px solid #DBDFE9'};
`;
